import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../utils/apiClient';
import { Globals } from '../globalfunctions.js';
import { subscribeToTicketEvents } from '../utils/ticketEventStream';
import { followUpPresets, describeDue, toLocalInput } from '../utils/followups';
import './FollowUps.css';

// "Remind me about this ticket" card for the ticket sidebar: quick presets,
// an optional note, or a custom time. Lists the open reminders on the ticket.
export default function FollowUps({ ticketId }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [custom, setCustom] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    apiClient.get(`/api/ticket-followups/ticket/${ticketId}`)
      .then((rows) => setItems(Array.isArray(rows) ? rows : []))
      .catch(() => setItems([]));
  }, [ticketId]);

  useEffect(() => { load(); }, [load]);
  // A reminder going off elsewhere should tick over here too.
  useEffect(() => subscribeToTicketEvents((e) => {
    if (e.type === 'followup-due' && e.payload && e.payload.ticket_id === ticketId) load();
  }), [ticketId, load]);

  const create = async (when) => {
    setBusy(true);
    setError('');
    try {
      await apiClient.post('/api/ticket-followups', { ticket_id: ticketId, due_at: new Date(when).toISOString(), note });
      setNote('');
      setCustom('');
      setOpen(false);
      load();
    } catch (err) {
      setError(err.message || 'Could not set the reminder.');
    } finally {
      setBusy(false);
    }
  };

  const finish = async (id, remove) => {
    try {
      if (remove) await apiClient.delete(`/api/ticket-followups/${id}`);
      else await apiClient.put(`/api/ticket-followups/${id}/done`, {});
      load();
    } catch (err) {
      setError(err.message || 'Could not update the reminder.');
    }
  };

  return (
    <div className="info-card fu-card">
      <h3>Follow-up</h3>
      {items.length > 0 && (
        <ul className="fu-list">
          {items.map((item) => {
            const due = describeDue(item.due_at);
            const mine = item.user_id === Globals.Id;
            return (
              <li key={item.id} className={due.overdue ? 'is-due' : ''}>
                <div className="fu-main">
                  <strong>{due.overdue ? 'Due now' : due.label}</strong>
                  <span>{mine ? 'You' : item.user_name}{item.note ? ` · ${item.note}` : ''}</span>
                </div>
                <button type="button" className="fu-icon" onClick={() => finish(item.id, false)} aria-label="Mark follow-up done" title="Done">
                  <i className="fas fa-check" aria-hidden="true"></i>
                </button>
                <button type="button" className="fu-icon" onClick={() => finish(item.id, true)} aria-label="Delete follow-up" title="Delete">
                  <i className="fas fa-xmark" aria-hidden="true"></i>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {!open ? (
        <button type="button" className="fu-add" onClick={() => setOpen(true)}>
          <i className="fas fa-bell" aria-hidden="true"></i> Remind me&hellip;
        </button>
      ) : (
        <div className="fu-form">
          <label className="sr-only" htmlFor={`fu-note-${ticketId}`}>Reminder note</label>
          <input id={`fu-note-${ticketId}`} type="text" maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional), e.g. chase for logins" />
          <div className="fu-presets">
            {followUpPresets().map((p) => (
              <button key={p.key} type="button" disabled={busy} onClick={() => create(p.at)}>{p.label}</button>
            ))}
          </div>
          <div className="fu-custom">
            <label className="sr-only" htmlFor={`fu-when-${ticketId}`}>Custom date and time</label>
            <input id={`fu-when-${ticketId}`} type="datetime-local" value={custom} min={toLocalInput(new Date())} onChange={(e) => setCustom(e.target.value)} />
            <button type="button" disabled={busy || !custom} onClick={() => create(custom)}>Set</button>
          </div>
          <button type="button" className="fu-cancel" onClick={() => { setOpen(false); setError(''); }}>Cancel</button>
        </div>
      )}
      {error && <p className="fu-error" role="alert">{error}</p>}
    </div>
  );
}
